/**
 * tRPC client for portal-ui → portal-bff communication.
 * Uses snake_case DTOs end-to-end.
 * Sends X-Request-Id for request correlation across logs.
 */

import type { AppRouter } from "@ramps-apps/ramps-portal-bff/trpc";
import { httpBatchLink } from "@trpc/client";
import { createTRPCReact } from "@trpc/react-query";
import { env } from "./config/env";

function requestId(): string {
	// nosemgrep: nodejs_scan.javascript-crypto-rule-node_insecure_random_generator -- fallback only for browsers without crypto.randomUUID; ID is a non-security request correlator
	return typeof crypto !== "undefined" && "randomUUID" in crypto
		? crypto.randomUUID()
		: `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export const trpc = createTRPCReact<AppRouter>();

export function createTrpcClient() {
	return trpc.createClient({
		links: [
			httpBatchLink({
				url: `${env.API_URL}/api/trpc`,
				headers() {
					return {
						"X-Request-Id": requestId(),
					};
				},
				fetch(url, options) {
					const headers = new Headers(options?.headers);
					if (!headers.has("X-Request-Id")) {
						headers.set("X-Request-Id", requestId());
					}
					// nosemgrep: nodejs_scan.javascript-ssrf-rule-node_ssrf -- url is constructed by tRPC client from env.API_URL (build-time) + static procedure paths
					return fetch(url, {
						...options,
						credentials: "include",
						headers,
					});
				},
			}),
		],
	});
}
